import type {
  TerminalClientMessage,
  TerminalServerMessage,
} from '@this+that/models';
import {
  TerminalClientMessageType,
  TerminalServerMessageType,
} from '@this+that/models';
import log from 'loglevel';

import { nextBackoffMs } from './backoff';
import { createListeners } from './createListeners';
import {
  type TerminalDevice,
  type TerminalGateway,
  TerminalSendError,
  type TerminalTransport,
} from './types';
import {
  TerminalConnectionStatus,
  TerminalSendFailure,
  TerminalTransportKind,
} from '../../../enums/terminal';
import {
  TERMINAL_ERROR_CODE_INVALID,
  TERMINAL_ERROR_CODE_SEND_FAILED,
  TERMINAL_ERROR_CODE_TOO_LARGE,
  TERMINAL_RELAY_KEEPALIVE_MS,
} from '../../../utils/constants/terminalConstants';

const VISIBILITY_EVENT = 'visibilitychange';

function toFailure(error: unknown): TerminalSendFailure {
  return error instanceof TerminalSendError
    ? error.failure
    : TerminalSendFailure.UNKNOWN;
}

export function createRelayTransport(
  gateway: TerminalGateway,
  device: TerminalDevice,
): TerminalTransport {
  const messages = createListeners<TerminalServerMessage>();
  const statuses = createListeners<TerminalConnectionStatus>();
  let isClosed = false;
  let pollAttempt = 0;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  let lastActivityAt = Date.now();
  let unsubscribeEvents: (() => void) | null = null;
  let unsubscribeReconnect: (() => void) | null = null;

  const emitError = (code: string, message: string) =>
    messages.emit({ type: TerminalServerMessageType.ERROR, code, message });

  const stopKeepalive = () => {
    if (keepaliveTimer) {
      clearInterval(keepaliveTimer);
      keepaliveTimer = null;
    }
  };

  const send = (message: TerminalClientMessage): Promise<void> =>
    gateway.sendMessage(device.deviceId, message).catch((error) => {
      handleSendError(error);
      throw error;
    });

  const sendQuietly = (message: TerminalClientMessage) => {
    send(message).catch(() => undefined);
  };

  const startKeepalive = () => {
    stopKeepalive();
    keepaliveTimer = setInterval(
      () => sendQuietly({ type: TerminalClientMessageType.SESSION_LIST }),
      TERMINAL_RELAY_KEEPALIVE_MS,
    );
  };

  const goOnline = () => {
    pollAttempt = 0;
    statuses.emit(TerminalConnectionStatus.ONLINE);
    startKeepalive();
  };

  const poll = () => {
    pollTimer = setTimeout(async () => {
      pollTimer = null;
      try {
        const devices = await gateway.listDevices();
        const current = devices.find(
          (item) => item.deviceId === device.deviceId,
        );
        if (isClosed) {
          return;
        }
        if (current?.isConnected) {
          goOnline();
          return;
        }
      } catch (error) {
        log.debug('relayTransport: device poll failed', error);
      }
      pollAttempt += 1;
      if (!isClosed) {
        poll();
      }
    }, nextBackoffMs(pollAttempt));
  };

  const goOffline = () => {
    stopKeepalive();
    statuses.emit(TerminalConnectionStatus.OFFLINE);
    if (!pollTimer) {
      poll();
    }
  };

  function handleSendError(error: unknown): void {
    if (isClosed) {
      return;
    }
    const failure = toFailure(error);
    switch (failure) {
      case TerminalSendFailure.DEVICE_OFFLINE:
        goOffline();
        break;
      case TerminalSendFailure.NOT_FOUND:
        stopKeepalive();
        statuses.emit(TerminalConnectionStatus.NO_DAEMON);
        break;
      case TerminalSendFailure.TOO_LARGE:
        emitError(TERMINAL_ERROR_CODE_TOO_LARGE, failure);
        break;
      case TerminalSendFailure.INVALID:
        emitError(TERMINAL_ERROR_CODE_INVALID, failure);
        break;
      default:
        log.error('relayTransport: send failed', error);
        emitError(TERMINAL_ERROR_CODE_SEND_FAILED, failure);
    }
  }

  const becomeReady = () => {
    if (device.isConnected) {
      goOnline();
    } else {
      goOffline();
    }
  };

  const handleResume = () => {
    const isStale = Date.now() - lastActivityAt >= TERMINAL_RELAY_KEEPALIVE_MS;
    if (document.visibilityState === 'visible' && isStale && !pollTimer) {
      lastActivityAt = Date.now();
      statuses.emit(TerminalConnectionStatus.ONLINE);
    }
  };

  return {
    connect: () => {
      unsubscribeEvents = gateway.subscribeEvents((payload) => {
        if (payload.deviceId === device.deviceId) {
          lastActivityAt = Date.now();
          messages.emit(payload.message);
        }
      });
      unsubscribeReconnect = gateway.subscribeSocketState((isConnected) => {
        if (isClosed) {
          return;
        }
        if (isConnected) {
          becomeReady();
        } else {
          stopKeepalive();
          statuses.emit(TerminalConnectionStatus.RECONNECTING);
        }
      });
      document.addEventListener(VISIBILITY_EVENT, handleResume);
      if (gateway.isSocketConnected()) {
        becomeReady();
      } else {
        statuses.emit(TerminalConnectionStatus.CONNECTING);
      }
    },
    send,
    onMessage: messages.subscribe,
    onStatus: statuses.subscribe,
    getKind: () => TerminalTransportKind.RELAY,
    getDeviceId: () => device.deviceId,
    close: () => {
      isClosed = true;
      stopKeepalive();
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
      unsubscribeEvents?.();
      unsubscribeReconnect?.();
      document.removeEventListener(VISIBILITY_EVENT, handleResume);
      messages.clear();
      statuses.clear();
    },
  };
}
